import { EnumTipoDomandaDTOStato } from '@framework/api-client/api/inquiries/schemas/enumTipoDomandaDTOStato.ts';
import { type TipoDomandaDTO } from '@framework/api-client/api/inquiries/schemas/tipoDomandaDTO.ts';
import { assertNever } from '@framework/engine/utils/assertNever.ts';
import { StyledTag } from '@framework/ui/components/tag/StyledTag.tsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { isInquiryReadonlyPreview } from '../isInquiryReadonlyPreview.ts';

export interface InquiryTypeStateTagProps {
  inquiry: TipoDomandaDTO;
  mini?: boolean;
}

/**
 * Stato di un Tipo Domanda
 */
export function InquiryTypeStateTag(props: InquiryTypeStateTagProps) {
  const { inquiry, mini } = props;
  const inquiryTag = useInquiryTag(inquiry);

  if (!inquiryTag) return null;

  return <StyledTag mini={mini} {...inquiryTag} />;
}

/**
 * Ritorna le impostazioni per il tag
 *
 */
function useInquiryTag(inquiry: TipoDomandaDTO) {
  const theme = useTheme();
  const { t } = useTranslation();

  const state = inquiry.stato;
  const isReadonlyPreview = isInquiryReadonlyPreview(inquiry);

  const tag = useMemo(() => {
    if (!state) return;

    // Caso speciale: Collaudo
    if (
      state === EnumTipoDomandaDTOStato.COLLAUDO ||
      (state === EnumTipoDomandaDTOStato.BOZZA &&
        inquiry.dataUltimaMessaInCollaudo)
    ) {
      return {
        color: '#9254de',
        children: t('stato.tipo-domanda.collaudo', 'COLLAUDO'),
      };
    }

    // Caso speciale: Anteprima in sola lettura (domanda pubblicata
    // ma non ancora attiva)
    if (isReadonlyPreview) {
      return {
        color: '#569bc4',
        children: t('stato.tipo-domanda.sola-lettura', 'SOLA LETTURA'),
      };
    }

    switch (state) {
      case EnumTipoDomandaDTOStato.BOZZA:
        return {
          color: '#757575',
          children: t('stato.tipo-domanda.bozza', 'BOZZA'),
        };
      case EnumTipoDomandaDTOStato.PRONTA:
        return {
          color: '#05263e',
          children: t('stato.tipo-domanda.pronta', 'PRONTA'),
        };
      case EnumTipoDomandaDTOStato.PUBBLICATA:
        return {
          color: theme?.colorInfo ?? '#569bc4',
          children: t('stato.tipo-domanda.pubblicata', 'PUBBLICATA'),
        };
      case EnumTipoDomandaDTOStato.CHIUSA:
        return {
          color: theme?.colorError ?? '#cf1322',
          children: t('stato.tipo-domanda.chiusa', 'CHIUSA'),
        };
      case EnumTipoDomandaDTOStato.APERTA:
        return {
          color: theme?.colorSuccess ?? '#389e0d',
          children: t('stato.tipo-domanda.aperta', 'APERTA'),
        };
      case EnumTipoDomandaDTOStato.IN_CORSO:
        return {
          color: '#13adcf',
          children: t('stato.tipo-domanda.in-corso', 'IN CORSO'),
        };
      case EnumTipoDomandaDTOStato.SOSPESA:
        return {
          color: '#4c6d82',
          children: t('stato.tipo-domanda.sospesa', 'SOSPESA'),
        };
      case EnumTipoDomandaDTOStato.ANNULLATA:
        return {
          color: theme?.colorWarning ?? '#faad14',
          children: t('stato.tipo-domanda.annullata', 'ANNULLATA'),
        };
    }
    assertNever(state);
  }, [
    inquiry.dataUltimaMessaInCollaudo,
    isReadonlyPreview,
    state,
    t,
    theme?.colorError,
    theme?.colorInfo,
    theme?.colorSuccess,
    theme?.colorWarning,
  ]);

  return tag;
}
